import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { 
  ArrowLeft, 
  CheckCircle2, 
  Building2, 
  Users, 
  User, 
  Handshake, 
  UserCircle,
  FileCheck,
  FileText,
  TrendingUp,
  AlertCircle,
  Receipt,
  Scale,
  Target,
  DollarSign,
  Phone,
  Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { serviceDetails } from '../data/serviceDetails';
import { firmInfo } from '../data/mock';

const iconMap = {
  Building2, Users, User, Handshake, UserCircle, FileCheck, FileText,
  TrendingUp, AlertCircle, Receipt, Scale, Target, DollarSign
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = serviceDetails[slug];

  // Scroll to top when component mounts or slug changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Service Not Found</h1>
          <Button onClick={() => navigate('/')}>Back to Home</Button>
        </div>
      </div>
    );
  }

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-200 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </Button>
            <Button onClick={scrollToContact} className="bg-teal-600 hover:bg-teal-700">
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              {service.title}
            </h1>
            <p className="text-xl text-teal-600 font-semibold mb-4">
              {service.subtitle}
            </p>
            <p className="text-lg text-gray-600">
              {service.description}
            </p>
          </div>
          
          {service.image && (
            <div className="max-w-5xl mx-auto">
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-[400px] object-cover rounded-2xl shadow-2xl"
              />
            </div>
          )}
        </div>
      </section>

      {/* Sub-Services Section */}
      {service.subServices && (
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {slug === 'company-registration' ? 'Types of Business Registration' : 'Our Service Offerings'}
              </h2>
              <p className="text-lg text-gray-600">
                Choose the right option for your business needs
              </p>
            </div>

            <div className="space-y-8">
              {service.subServices.map((subService) => {
                const Icon = iconMap[subService.icon] || CheckCircle2;
                return (
                  <Card key={subService.id} className="overflow-hidden hover:shadow-xl transition-shadow">
                    <CardHeader className="bg-gradient-to-r from-teal-50 to-white">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 bg-teal-600 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Icon className="w-7 h-7 text-white" />
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-2xl mb-2">{subService.name}</CardTitle>
                          <CardDescription className="text-base">{subService.description}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                      <div className="grid md:grid-cols-2 gap-8">
                        {subService.benefits && (
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                              <CheckCircle2 className="w-5 h-5 text-teal-600" />
                              Key Benefits
                            </h4>
                            <ul className="space-y-2">
                              {subService.benefits.map((benefit, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-gray-700">
                                  <span className="text-teal-600 mt-1">•</span>
                                  <span>{benefit}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {subService.requirements && (
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-4">Requirements</h4>
                            <ul className="space-y-2">
                              {subService.requirements.map((req, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-gray-700">
                                  <span className="text-teal-600 mt-1">•</span>
                                  <span>{req}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {subService.details && (
                          <div className="md:col-span-2">
                            <ul className="grid md:grid-cols-2 gap-3">
                              {subService.details.map((detail, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-gray-700">
                                  <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                                  <span>{detail}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                      
                      {subService.suitableFor && (
                        <div className="mt-6 p-4 bg-teal-50 rounded-lg">
                          <p className="text-sm font-semibold text-teal-900 mb-1">Best Suited For:</p>
                          <p className="text-sm text-teal-800">{subService.suitableFor}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Our Services Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              What We Offer
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {service.ourServices.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-white p-4 rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      {service.process && (
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
                Our Process
              </h2>
              <div className="relative">
                {/* Timeline line */}
                <div className="hidden md:block absolute left-8 top-0 bottom-0 w-0.5 bg-teal-200"></div>
                
                <div className="space-y-8">
                  {service.process.map((step) => (
                    <div key={step.step} className="relative flex items-start gap-6">
                      <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg z-10">
                        {step.step}
                      </div>
                      <div className="flex-1 bg-white p-6 rounded-lg shadow-md">
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{step.title}</h3>
                        <p className="text-gray-600">{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-600 to-teal-700">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-xl mb-8 text-teal-50">
              Contact us today for expert guidance and hassle-free service
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={scrollToContact}
                className="bg-white text-teal-600 hover:bg-gray-100 text-lg h-14 px-8"
              >
                Schedule Consultation
              </Button>
              <a href={`tel:${firmInfo.phone}`}>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 text-lg h-14 px-8 w-full sm:w-auto"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call Us Now
                </Button>
              </a>
            </div>
            <div className="mt-8 flex items-center justify-center gap-6 text-teal-50">
              <div className="flex items-center gap-2">
                <Phone className="w-5 h-5" />
                <span>{firmInfo.phone}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                <span>{firmInfo.email}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
